import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { themeBlue, themeOrange } from "../../utils/colorTheme";
import { apiHandle } from "../../config/apiHandle/apiHandle";
import { IoSearch } from "react-icons/io5";
import { FiUsers } from "react-icons/fi";
import { MdCheckCircle, MdCancel } from "react-icons/md";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.replace(" ", "T") + (iso.endsWith("Z") ? "" : "Z"));
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const DashboardProContracts = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pros, setPros] = useState([]);
  const [summary, setSummary] = useState({ total: 0, signed: 0, unsigned: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiHandle
      .get("/admin/pro-contracts")
      .then((res) => {
        if (cancelled) return;
        setPros(res.data?.pros || []);
        setSummary(res.data?.summary || { total: 0, signed: 0, unsigned: 0 });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err.message || "Failed to load");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return pros.filter((p) => {
      if (filter === "signed" && !p.signed_at) return false;
      if (filter === "unsigned" && p.signed_at) return false;
      if (!q) return true;
      const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
      return (
        name.includes(q) ||
        (p.email || "").toLowerCase().includes(q) ||
        (p.phone || "").toLowerCase().includes(q) ||
        (p.signed_name || "").toLowerCase().includes(q)
      );
    });
  }, [pros, searchQuery, filter]);

  const statCards = [
    {
      label: "Total Pros",
      value: summary.total,
      icon: <FiUsers size={20} />,
      bg: "#EDE7F6",
      color: "#5E35B1",
      filterKey: "all",
    },
    {
      label: "Signed",
      value: summary.signed,
      icon: <MdCheckCircle size={20} />,
      bg: "#E8F5E9",
      color: "#2E7D32",
      filterKey: "signed",
    },
    {
      label: "Unsigned",
      value: summary.unsigned,
      icon: <MdCancel size={20} />,
      bg: "#FFEBEE",
      color: "#C62828",
      filterKey: "unsigned",
    },
  ];

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="60vh">
        <CircularProgress size={30} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  return (
    <Stack sx={{ bgcolor: "#F8F9FA", minHeight: "100vh" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2.5 }}
      >
        <Stack>
          <Typography sx={{ fontSize: "24px", fontWeight: 700, color: themeBlue }}>
            Provider Agreements
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#999", mt: 0.3 }}>
            Track which pros have signed the non-circumvention agreement
          </Typography>
        </Stack>
      </Stack>

      <Container sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" } }}>
        {error && (
          <Box sx={{ bgcolor: "#FFEBEE", color: "#C62828", p: 2, borderRadius: "10px", mb: 2 }}>
            {error}
          </Box>
        )}

        <Grid container spacing={2} mb={3}>
          {statCards.map((stat, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                onClick={() => setFilter(stat.filterKey)}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "14px",
                  p: 2.5,
                  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                  cursor: "pointer",
                  border: filter === stat.filterKey ? `2px solid ${stat.color}` : "2px solid transparent",
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.1)" },
                }}
              >
                <Box
                  sx={{
                    bgcolor: stat.bg,
                    color: stat.color,
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {stat.icon}
                </Box>
                <Stack>
                  <Typography sx={{ fontSize: "12px", color: "#888" }}>{stat.label}</Typography>
                  <Typography sx={{ fontSize: "22px", fontWeight: 700, color: themeBlue }}>
                    {stat.value}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name, email, phone…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ bgcolor: "#fff", borderRadius: "10px", width: { xs: "100%", sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoSearch size={16} color="#999" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <TableContainer sx={{ bgcolor: "#fff", borderRadius: "14px", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8F9FA" }}>
                <TableCell sx={{ fontWeight: 600, color: "#666" }}>Pro</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#666" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#666" }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#666" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#666" }}>Signed At</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#666" }}>Signed Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#666" }}>Version</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "#999", py: 4 }}>
                    No pros match the current filter.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                      {`${p.first_name || ""} ${p.last_name || ""}`.trim() || `#${p.id}`}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#999" }}>ID {p.id}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>{p.email || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>{p.phone || "—"}</TableCell>
                  <TableCell>
                    {p.signed_at ? (
                      <Chip
                        size="small"
                        label="Signed"
                        sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 600 }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="Unsigned"
                        sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 600 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>{formatDate(p.signed_at)}</TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>{p.signed_name || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>{p.contract_version || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Stack>
  );
};

export default DashboardProContracts;
